import {
    areaValueForRow,
    type CoverageArea,
} from '@apps/web/features/coverage/model/area_model';
import {
    type SlotAvailability,
    summarizeSlotAvailability,
} from '@apps/web/features/coverage/model/cell_model';
import {
    type CoverageRow,
    type DayPartKey,
    type WorkWeekStartDay,
} from '@apps/web/features/coverage/model/coverage_types';
import {
    type CoverageSelection,
    slotsForArea,
} from '@apps/web/features/coverage/model/selection_model';

export type AskAudienceKey =
    | 'missing_information'
    | 'unavailable'
    | 'available';

export const ASK_AUDIENCE_LABELS: Record<AskAudienceKey, string> = {
    missing_information: 'Missing information',
    unavailable: 'Unavailable',
    available: 'Available',
};

export const ASK_AUDIENCE_DEFAULT_ON: Record<AskAudienceKey, boolean> = {
    missing_information: true,
    unavailable: false,
    available: false,
};

export const ASK_AUDIENCE_ORDER: ReadonlyArray<AskAudienceKey> = [
    'missing_information',
    'unavailable',
    'available',
];

export type AskCandidate = {
    readonly caregiverId: string;
    readonly name: string;
    readonly availability: SlotAvailability;
    readonly hasUnknownSlots: boolean;
};

export function askCandidateMatchesAudience(
    candidate: AskCandidate,
    audience: AskAudienceKey,
): boolean {
    return audience === 'missing_information'
        ? candidate.hasUnknownSlots
        : candidate.availability === audience;
}

export function buildAskCandidates(
    rows: ReadonlyArray<CoverageRow>,
    selection: CoverageSelection,
    area: CoverageArea,
    workWeekStartDay: WorkWeekStartDay,
): ReadonlyArray<AskCandidate> {
    // Per area: a cell clicked for one area must not judge another's rows.
    const slotsByArea = new Map<
        string,
        ReadonlyArray<readonly [number, DayPartKey]>
    >();
    const byCaregiver = new Map<string, AskCandidate>();
    for (const row of rows) {
        if (byCaregiver.has(row.caregiver_id)) {
            continue;
        }
        const areaKey = areaValueForRow(row, area).key;
        let slots = slotsByArea.get(areaKey);
        if (slots === undefined) {
            slots = slotsForArea(selection, areaKey, workWeekStartDay);
            slotsByArea.set(areaKey, slots);
        }
        const summary = summarizeSlotAvailability(row, slots);
        if (summary === null) {
            continue;
        }
        byCaregiver.set(row.caregiver_id, {
            caregiverId: row.caregiver_id,
            name: row.name,
            ...summary,
        });
    }
    return [...byCaregiver.values()];
}

/** De-duplicates by caregiver: one person hit by several slots gets one text. */
export function resolveAskRecipients(
    candidates: ReadonlyArray<AskCandidate>,
    enabledAudiences: ReadonlySet<AskAudienceKey>,
): ReadonlyArray<AskCandidate> {
    const byCaregiver = new Map<string, AskCandidate>();
    const audiences = [...enabledAudiences];
    for (const candidate of candidates) {
        if (
            !audiences.some((audience) =>
                askCandidateMatchesAudience(candidate, audience),
            )
        ) {
            continue;
        }
        if (!byCaregiver.has(candidate.caregiverId)) {
            byCaregiver.set(candidate.caregiverId, candidate);
        }
    }
    return [...byCaregiver.values()];
}
